import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getDataPlans, buyData } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const NETWORKS = [
  { id: "MTN", label: "MTN", logo: "🟡" },
  { id: "AIRTEL", label: "Airtel", logo: "🔴" },
  { id: "GLO", label: "Glo", logo: "🟢" },
  { id: "9MOBILE", label: "9mobile", logo: "🟩" },
];

export default function DataPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [network, setNetwork] = useState(searchParams.get("network") || "MTN");
  const [phone, setPhone] = useState("");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const isValid = phone.length === 11 && !!selectedPlan;

  // Fetch live plans whenever network changes
  useEffect(() => {
    const fetchPlans = async () => {
      setPlansLoading(true);
      setSelectedPlan(null);
      setPlans([]);
      try {
        const res = await getDataPlans(network);
        if (res.status) {
          setPlans(res.plans || []);
        } else {
          toast.error("Could not load data plans");
        }
      } catch (err) {
        toast.error("Failed to fetch data plans");
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, [network]);

  const handlePay = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    try {
      const res = await buyData(
        network,
        Number(phone),
        selectedPlan.variationCode,
        Number(selectedPlan.amount),
        // user?.phone
        "07014970207"
      );

      if (res.status) {
        navigate("/bills/success", {
          state: {
            type: "data",
            title: `${network} ${selectedPlan.name}`,
            detail: phone,
            amount: Number(selectedPlan.amount),
            requestId: res.requestId,
            transactionId: res.transactionId,
          },
        });
      } else {
        toast.error(res.message || "Data purchase failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Data purchase failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Buy Data</h1>
            <p className="text-slate-400 text-xs">All networks, all bundles</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Network */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Select Network</p>
          <div className="grid grid-cols-4 gap-2">
            {NETWORKS.map((net) => (
              <button
                key={net.id}
                onClick={() => setNetwork(net.id)}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  network === net.id ? "border-emerald-500 bg-emerald-50" : "border-slate-100 bg-white"
                }`}
              >
                <span className="text-2xl">{net.logo}</span>
                <span className="text-xs font-semibold text-slate-700">{net.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Phone */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Phone Number</p>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors">
            <span className="text-slate-400 text-sm mr-2">🇳🇬 +234</span>
            <div className="w-px h-5 bg-slate-200 mr-3" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
              placeholder="0801 234 5678"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
            {phone.length === 11 && <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />}
          </div>
        </div>

        {/* Plans */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Choose Plan</p>

          {plansLoading ? (
            <div className="grid grid-cols-2 gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 bg-white rounded-2xl border border-slate-100 animate-pulse" />
              ))}
            </div>
          ) : plans.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
              <p className="text-slate-400 text-sm">No plans available for {network}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {plans.map((plan) => (
                <button
                  key={plan.variationCode}
                  onClick={() => setSelectedPlan(plan)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    selectedPlan?.variationCode === plan.variationCode
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-slate-100 bg-white"
                  }`}
                >
                  <p className="font-bold text-slate-800 text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {plan.name}
                  </p>
                  <p className="text-emerald-600 font-bold text-sm mt-2">
                    ₦{Number(plan.amount).toLocaleString()}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {isValid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2">
            {[
              { label: "Network", value: network },
              { label: "Plan", value: selectedPlan.name },
              { label: "Number", value: phone },
              { label: "Total", value: `₦${Number(selectedPlan.amount).toLocaleString()}`, bold: true },
            ].map((row) => (
              <div key={row.label} className={`flex justify-between text-sm ${row.bold ? "border-t border-emerald-200 pt-2 mt-2" : ""}`}>
                <span className="text-slate-500">{row.label}</span>
                <span className={row.bold ? "font-bold text-emerald-600" : "font-semibold text-slate-800"}>{row.value}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handlePay}
          disabled={!isValid || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
          ) : isValid ? `Pay ₦${Number(selectedPlan.amount).toLocaleString()}` : "Select a plan to continue"}
        </button>
      </div>
    </div>
  );
}
